import type { AxiosInstance } from 'axios';
import type {
  CompanyResponse,
  GetSupportWidgetIdResponse,
  UpdateCompanyRequest,
  UpdateSupportWidgetIdRequest,
  UpdateSupportWidgetIdResponse,
} from './contracts/company';
import type {
  CompanyBooleanConfigKey,
  CompanyConfigKey,
  CompanyConfigResponse,
  CompanyJsonConfigKey,
  CompanyStringConfigKey,
  CompanyWithConfigsResponse,
  StrongPasswordOptionsResponse,
  UpdateCompanyBooleanConfigRequest,
  UpdateCompanyJsonConfigRequest,
  UpdateCompanyStringConfigRequest,
} from './contracts/company-config';
import type { CompanyQueue } from './contracts/company-queue';
import type { WorkGroupListResponse, WorkGroupMembersResponse, WorkGroupResponse } from './contracts/work-group';
import type {
  GetAllUsersParams,
  GetAllUsersResponse,
  GetManagedUsersResponse,
  GetUserByIdResponse,
  ListUsersRequest,
  ListUsersResponse,
  UpdateCompanyUserAvatarResponse,
  UpdateCompanyUserRequest,
  UpdateUserPasswordRequest,
  UpdateUserPasswordResponse,
  User,
  UserProfile,
} from './contracts/user';
import type {
  ActiveAnnouncement,
  ActiveAnnouncementQuery,
  Announcement,
  AnnouncementListFilters,
  AnnouncementListResponse,
  AnnouncementType,
  CreateAnnouncementRequest,
  UpdateAnnouncementRequest,
  UpdateAnnouncementStatusRequest,
} from './contracts/announcement';
import type {
  GenerateTwoFactorOtpRequest,
  GenerateTwoFactorOtpResponse,
  ValidateTwoFactorOtpRequest,
  ValidateTwoFactorOtpResponse,
} from './contracts/authenticate';

/**
 * Gateway for interacting with the Customer Service API.
 */
export class CustomerServiceGateway {
  constructor(
    private readonly httpClient: AxiosInstance,
    private readonly baseUrl: string,
  ) {}

  /**
   * Retrieves a company by its unique identifier.
   * @param companyId - The unique identifier of the company.
   * @returns The company data wrapped in a {@link CompanyResponse}.
   */
  async getCompanyById(companyId: string): Promise<CompanyResponse> {
    const { data } = await this.httpClient.get<CompanyResponse>(`${this.baseUrl}/companies/search/${companyId}`);
    return data;
  }

  /**
   * Updates the current company settings.
   * @param payload - The company fields to update.
   * @returns The updated company data wrapped in a {@link CompanyResponse}.
   */
  async updateCompany(payload: UpdateCompanyRequest): Promise<CompanyResponse> {
    const { data } = await this.httpClient.put<CompanyResponse>(`${this.baseUrl}/companies`, payload);
    return data;
  }

  /**
   * Retrieves the support widget identifier for the current company.
   * @returns The support widget identifier.
   */
  async getSupportWidgetId(): Promise<GetSupportWidgetIdResponse> {
    const { data } = await this.httpClient.get<GetSupportWidgetIdResponse>(
      `${this.baseUrl}/companies/support-widget-id`,
    );
    return data;
  }

  /**
   * Updates the support widget identifier for the current company.
   * @param payload - The support widget identifier to set.
   * @returns The updated company support widget data.
   */
  async updateSupportWidgetId(payload: UpdateSupportWidgetIdRequest): Promise<UpdateSupportWidgetIdResponse> {
    const { data } = await this.httpClient.put<UpdateSupportWidgetIdResponse>(
      `${this.baseUrl}/companies/support-widget-id`,
      payload,
    );
    return data;
  }

  /**
   * Retrieves the list of queues configured for the current company.
   * @returns The company queues.
   */
  async getCompanyQueues(): Promise<CompanyQueue[]> {
    const { data } = await this.httpClient.get<CompanyQueue[]>(`${this.baseUrl}/companies/queues`);
    return data;
  }

  /**
   * Retrieves the current company data along with all its config entries.
   * @returns The company data with its configs.
   */
  async getCompanyConfigs(): Promise<CompanyWithConfigsResponse> {
    const { data } = await this.httpClient.get<CompanyWithConfigsResponse>(`${this.baseUrl}/companies/configs`);
    return data;
  }

  /**
   * Retrieves a company config entry by key.
   * @param key - The config key to fetch.
   * @returns The company config entry.
   */
  async getCompanyConfig(key: 'strong-password-options'): Promise<StrongPasswordOptionsResponse>;
  async getCompanyConfig(key: CompanyConfigKey): Promise<CompanyConfigResponse>;
  async getCompanyConfig(key: CompanyConfigKey): Promise<CompanyConfigResponse | StrongPasswordOptionsResponse> {
    const { data } = await this.httpClient.get<CompanyConfigResponse | StrongPasswordOptionsResponse>(
      `${this.baseUrl}/companies/configs/${key}`,
    );
    return data;
  }

  /**
   * Retrieves the strong password options config for a company.
   * @param companyId - The unique identifier of the company.
   * @returns The strong password options company config.
   */
  async getStrongPasswordOptions(companyId: string): Promise<StrongPasswordOptionsResponse> {
    const { data } = await this.httpClient.get<StrongPasswordOptionsResponse>(
      `${this.baseUrl}/companies/strong-password-options`,
      { params: { companyId } },
    );
    return data;
  }

  /**
   * Updates a JSON company config by key.
   * @param key - The config key to update.
   * @param payload - The JSON value to set.
   * @returns The updated company config entry.
   */
  async updateCompanyJsonConfig(
    key: CompanyJsonConfigKey,
    payload: UpdateCompanyJsonConfigRequest,
  ): Promise<CompanyConfigResponse> {
    const { data } = await this.httpClient.put<CompanyConfigResponse>(
      `${this.baseUrl}/companies/configs/${key}`,
      payload,
    );
    return data;
  }

  /**
   * Updates a boolean company config by key.
   * @param key - The config key to update.
   * @param payload - The boolean value to set.
   * @returns The updated company config entry.
   */
  async updateCompanyBooleanConfig(
    key: CompanyBooleanConfigKey,
    payload: UpdateCompanyBooleanConfigRequest,
  ): Promise<CompanyConfigResponse> {
    const { data } = await this.httpClient.put<CompanyConfigResponse>(
      `${this.baseUrl}/companies/configs/${key}`,
      payload,
    );
    return data;
  }

  /**
   * Updates a string company config by key.
   * @param key - The config key to update.
   * @param payload - The string value to set.
   * @returns The updated company config entry.
   */
  async updateCompanyStringConfig(
    key: CompanyStringConfigKey,
    payload: UpdateCompanyStringConfigRequest,
  ): Promise<CompanyConfigResponse> {
    const { data } = await this.httpClient.put<CompanyConfigResponse>(
      `${this.baseUrl}/companies/configs/${key}`,
      payload,
    );
    return data;
  }

  /**
   * Retrieves a user by login email.
   * @param email - The user email used as login identifier.
   * @returns The user data.
   */
  async getUserByLogin(email: string): Promise<User> {
    const { data } = await this.httpClient.get<User>(`${this.baseUrl}/users/login/${email}`);
    return data;
  }

  /**
   * Lists company users with filters, pagination and optional associations.
   * Company is resolved from the authenticated token, or from `companyId` when the token has none.
   * @param query - Pagination, filters and `include` associations.
   * @returns Paginated users.
   */
  async listUsers(query?: ListUsersRequest): Promise<ListUsersResponse> {
    const { data } = await this.httpClient.get<ListUsersResponse>(`${this.baseUrl}/users`, {
      params: query,
    });
    return data;
  }

  /**
   * Retrieves all work groups for a company.
   * @param companyId - The unique identifier of the company.
   * @returns The work groups for the given company.
   */
  async getWorkGroups(companyId: string): Promise<WorkGroupListResponse> {
    const { data } = await this.httpClient.get<WorkGroupListResponse>(`${this.baseUrl}/companies/work-groups`, {
      params: { company_id: companyId },
    });
    return data;
  }

  /**
   * Retrieves a work group by its unique identifier.
   * @param workGroupId - The unique identifier of the work group.
   * @param companyId - The unique identifier of the company (optional, sent as query parameter).
   * @returns The work group data wrapped in a {@link WorkGroupResponse}.
   */
  async getWorkGroupById(workGroupId: string, companyId?: string): Promise<WorkGroupResponse> {
    const params = companyId ? { company_id: companyId } : undefined;
    const { data } = await this.httpClient.get<WorkGroupResponse>(
      `${this.baseUrl}/companies/work-groups/${workGroupId}`,
      { params },
    );
    return data;
  }

  /**
   * Retrieves the members of a work group by its identifier.
   * @param groupId - The unique identifier of the work group.
   * @param companyId - The unique identifier of the company (optional, sent as query parameter).
   * @param profile - Filter members by profile (optional).
   * @returns The members of the work group.
   */
  async getWorkGroupMembers(
    groupId: string,
    companyId?: string,
    profile?: UserProfile[],
  ): Promise<WorkGroupMembersResponse> {
    const params: { company_id?: string; profile?: UserProfile[] } = {};
    if (companyId) params.company_id = companyId;
    if (profile) params.profile = profile;
    const { data } = await this.httpClient.get<WorkGroupMembersResponse>(
      `${this.baseUrl}/bonds/work-groups/${groupId}`,
      { params },
    );
    return data;
  }

  async getAllUsers(params?: GetAllUsersParams): Promise<GetAllUsersResponse> {
    const { data } = await this.httpClient.get<GetAllUsersResponse>(`${this.baseUrl}/companies/users`, { params });
    return data;
  }

  async getManagedUsers(managerId: string): Promise<GetManagedUsersResponse> {
    const { data } = await this.httpClient.get<GetManagedUsersResponse>(`${this.baseUrl}/users/managed/${managerId}`);
    return data;
  }

  /**
   * Retrieves a user by its unique identifier.
   * @param userId - The unique identifier of the user.
   * @returns The user data wrapped in a {@link GetUserByIdResponse}.
   */
  async getUserById(userId: string): Promise<GetUserByIdResponse> {
    const { data } = await this.httpClient.get<GetUserByIdResponse>(`${this.baseUrl}/companies/users/search/${userId}`);
    return data;
  }

  /**
   * Updates a company user by its unique identifier.
   * @param userId - The unique identifier of the user.
   * @param payload - The user fields to update.
   * @returns The updated user data.
   */
  async updateCompanyUser(userId: string, payload: UpdateCompanyUserRequest): Promise<User> {
    const { data } = await this.httpClient.put<User>(`${this.baseUrl}/companies/users/${userId}`, payload);
    return data;
  }

  /**
   * Updates the current user's password.
   * @param payload - The current password and the new password.
   * @returns The updated user password data.
   */
  async updateUserPassword(payload: UpdateUserPasswordRequest): Promise<UpdateUserPasswordResponse> {
    const { data } = await this.httpClient.put<UpdateUserPasswordResponse>(
      `${this.baseUrl}/companies/users/password`,
      payload,
    );
    return data;
  }

  /**
   * Updates a company user's avatar image.
   * @param userId - The unique identifier of the user.
   * @param formData - Multipart form data containing the `avatar` file field.
   * @returns Whether the avatar was updated successfully.
   */
  async updateCompanyUserAvatar(userId: string, formData: any): Promise<UpdateCompanyUserAvatarResponse> {
    const { data } = await this.httpClient.put<UpdateCompanyUserAvatarResponse>(
      `${this.baseUrl}/v2/companies/users/avatar/${userId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return data;
  }

  /**
   * Generates a two-factor OTP secret URL for a user.
   * @param payload - The user identifier.
   * @returns The OTP provisioning URL.
   */
  async generateTwoFactorOtp(payload: GenerateTwoFactorOtpRequest): Promise<GenerateTwoFactorOtpResponse> {
    const { data } = await this.httpClient.post<GenerateTwoFactorOtpResponse>(
      `${this.baseUrl}/authenticate/twoFactor/otp`,
      payload,
    );
    return data;
  }

  /**
   * Validates a two-factor OTP code for a user.
   * @param payload - The user identifier and OTP code.
   * @returns Whether the OTP code is valid.
   */
  async validateTwoFactorOtp(payload: ValidateTwoFactorOtpRequest): Promise<ValidateTwoFactorOtpResponse> {
    const { data } = await this.httpClient.post<ValidateTwoFactorOtpResponse>(
      `${this.baseUrl}/authenticate/twoFactor/otp/validate`,
      payload,
    );
    return data;
  }

  /**
   * Logs out the current user.
   */
  async logout(): Promise<void> {
    await this.httpClient.get(`${this.baseUrl}/users/logout`);
  }

  /**
   * Retrieves a paginated list of announcements.
   * @param query - Optional filters and pagination parameters.
   * @returns The paginated announcements response.
   */
  async findAnnouncements(query?: AnnouncementListFilters): Promise<AnnouncementListResponse> {
    const { data } = await this.httpClient.get<AnnouncementListResponse>(`${this.baseUrl}/announcements/admin`, {
      params: query,
    });
    return data;
  }

  /**
   * Retrieves an announcement by its unique identifier.
   * @param announcementId - The unique identifier of the announcement.
   * @returns The announcement data.
   */
  async getAnnouncement(announcementId: string): Promise<Announcement> {
    const { data } = await this.httpClient.get<Announcement>(`${this.baseUrl}/announcements/admin/${announcementId}`);
    return data;
  }

  /**
   * Creates a new announcement.
   * @param payload - The announcement data to create.
   * @returns The created announcement.
   */
  async createAnnouncement(payload: CreateAnnouncementRequest): Promise<Announcement> {
    const { data } = await this.httpClient.post<Announcement>(`${this.baseUrl}/announcements/admin`, payload);
    return data;
  }

  /**
   * Updates an existing announcement.
   * @param announcementId - The unique identifier of the announcement.
   * @param payload - The announcement data to update.
   * @returns The updated announcement.
   */
  async updateAnnouncement(announcementId: string, payload: UpdateAnnouncementRequest): Promise<Announcement> {
    const { data } = await this.httpClient.put<Announcement>(
      `${this.baseUrl}/announcements/admin/${announcementId}`,
      payload,
    );
    return data;
  }

  /**
   * Toggles the active status of an announcement.
   * @param announcementId - The unique identifier of the announcement.
   * @param payload - The new active status.
   * @returns The updated announcement.
   */
  async updateAnnouncementStatus(
    announcementId: string,
    payload: UpdateAnnouncementStatusRequest,
  ): Promise<Announcement> {
    const { data } = await this.httpClient.patch<Announcement>(
      `${this.baseUrl}/announcements/admin/${announcementId}/status`,
      payload,
    );
    return data;
  }

  /**
   * Removes an announcement by its unique identifier.
   * @param announcementId - The unique identifier of the announcement.
   */
  async deleteAnnouncement(announcementId: string): Promise<void> {
    await this.httpClient.delete(`${this.baseUrl}/announcements/admin/${announcementId}`);
  }

  /**
   * Retrieves all available announcement types.
   * @returns Array of announcement types.
   */
  async getAnnouncementTypes(): Promise<AnnouncementType[]> {
    const { data } = await this.httpClient.get<AnnouncementType[]>(`${this.baseUrl}/announcements/admin/types`);
    return data;
  }

  /**
   * Retrieves active announcements tailored for the widget context.
   * @param query - The company and environment context.
   * @returns The active announcements for the widget.
   */
  async findActiveAnnouncements(query: ActiveAnnouncementQuery): Promise<ActiveAnnouncement[]> {
    const { data } = await this.httpClient.get<ActiveAnnouncement[]>(`${this.baseUrl}/announcements/active`, {
      params: query,
    });
    return data;
  }
}

export type {
  CompanyResponse,
  CompanyData,
  UpdateCompanyRequest,
  UpdateSupportWidgetIdRequest,
  UpdateSupportWidgetIdResponse,
  GetSupportWidgetIdResponse,
} from './contracts/company';
export type {
  StrongPasswordOptions,
  StrongPasswordOptionsResponse,
  CompanyConfigResponse,
  CompanyConfigKey,
  CompanyWithConfigsResponse,
  CompanyBooleanConfigKey,
  CompanyJsonConfigKey,
  CompanyStringConfigKey,
  UpdateCompanyBooleanConfigRequest,
  UpdateCompanyJsonConfigRequest,
  UpdateCompanyStringConfigRequest,
} from './contracts/company-config';
export type { CompanyQueue } from './contracts/company-queue';
export type {
  WorkGroup,
  WorkGroupMember,
  WorkGroupMembersResponse,
  WorkGroupListResponse,
  WorkGroupResponse,
} from './contracts/work-group';
export type {
  User,
  UserProfile,
  GetAllUsersParams,
  GetAllUsersResponse,
  ManagedUser,
  GetManagedUsersResponse,
  GetUserByIdResponse,
  ListUsersStatus,
  ListUsersProfile,
  ListUsersType,
  ListUsersInclude,
  ListUsersRequest,
  UserBond,
  ListUsersResponse,
} from './contracts/user';
export type {
  UpdateCompanyUserRequest,
  UpdateCompanyUserAvatarResponse,
  UpdateUserPasswordRequest,
  UpdateUserPasswordResponse,
} from './contracts/user';
export type {
  GenerateTwoFactorOtpRequest,
  GenerateTwoFactorOtpResponse,
  ValidateTwoFactorOtpRequest,
  ValidateTwoFactorOtpResponse,
} from './contracts/authenticate';
export type {
  Announcement,
  AnnouncementAppearance,
  AnnouncementVisualStatus,
  AnnouncementButton,
  AnnouncementCompany,
  AnnouncementListResponse,
  AnnouncementListFilters,
  AnnouncementPayload,
  AnnouncementTargetingRuleUrl,
  AnnouncementTargetingRules,
  AnnouncementType,
  CreateAnnouncementRequest,
  UpdateAnnouncementRequest,
  UpdateAnnouncementStatusRequest,
  ActiveAnnouncement,
  ActiveAnnouncementQuery,
} from './contracts/announcement';
